import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { RootState } from '../store';
import { WorkflowStage, setFormulation } from '../store/slices/workflowSlice';
import { useAppDispatch } from '../hooks/reduxHooks';
import WorkflowLayout from '../components/workflow/WorkflowLayout';
import DatabaseFormulationSelector from '../components/formulation/DatabaseFormulationSelector';
import FormulationAnalysis from '../components/formulation/FormulationAnalysis';
import FormulationCreator from '../components/formulation/FormulationCreator';

// Tab panel component for accessibility
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`formulation-tabpanel-${index}`}
      aria-labelledby={`formulation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `formulation-tab-${index}`,
    'aria-controls': `formulation-tabpanel-${index}`,
  };
}

const FormulationWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get workflow state
  const workflowState = useSelector((state: RootState) => state.workflow);
  const isStage2Completed = workflowState.stageStatus[WorkflowStage.CROP_FORMULATION]?.completed || false;
  const workflowFormulation = workflowState.formulation;
  const waterProfile = workflowState.waterProfile;

  const [activeTab, setActiveTab] = useState(0);
  const [selectedFormulation, setSelectedFormulation] = useState<any>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFormulationSelect = useCallback((formulation: any) => {
    setSelectedFormulation(formulation);
    setShowCreateForm(false);

    if (formulation) {
      // Convert database formulation to component format if needed
      const formulationData = {
        id: formulation.id,
        name: formulation.name,
        description: formulation.description,
        cropType: formulation.cropType,
        targetEc: formulation.targetEc,
        targetPh: formulation.targetPh,
        volume: formulation.volume,
        volumeUnit: formulation.volumeUnit,
        elements: formulation.targetElements?.map((element: any) => ({
          symbol: element.element?.symbol || element.elementSymbol,
          name: element.element?.name || element.elementName,
          targetConcentration: element.targetConcentration || element.concentration,
          unit: element.unit
        })) || []
      };

      setSelectedFormulation(formulationData);
    }
  }, []);

  const handleCreateNewFormulation = useCallback(() => {
    setShowCreateForm(true);
    setSelectedFormulation(null);
  }, []);

  const handleFormulationSave = useCallback((formulationData: any) => {
    console.log('Custom formulation saved:', formulationData);

    const workflowFormulation = {
      id: formulationData.id || `custom-${Date.now()}`,
      name: formulationData.name || 'Custom Formulation',
      cropType: 'Custom',
      description: formulationData.description || '',
      elements: formulationData.elements || [],
      scientificReference: 'User-defined formulation'
    };

    setSelectedFormulation(workflowFormulation);
  }, []);

  const handleConfirmFormulation = useCallback(() => {
    if (!selectedFormulation) return;

    // Convert to workflow format
    const workflowFormulation = {
      id: selectedFormulation.id,
      name: selectedFormulation.name,
      cropType: selectedFormulation.cropType || 'General',
      description: selectedFormulation.description || '',
      elements: selectedFormulation.elements || [],
      scientificReference: selectedFormulation.scientificReference || 'Database formulation'
    };

    // Save to workflow state (this automatically marks Stage 2 as completed)
    dispatch(setFormulation(workflowFormulation));

    // Navigate to next stage
    navigate('/salt-library');
  }, [selectedFormulation, dispatch, navigate]);

  return (
    <WorkflowLayout
      stage={WorkflowStage.CROP_FORMULATION}
      title="Crop"
      description="Target nutrient concentrations for the crop or growth stage."
    >
      {waterProfile && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Water: {waterProfile.name} · pH {waterProfile.ph} · EC {waterProfile.ec} mS/cm
        </Typography>
      )}

      {/* Main Content */}
      <Paper variant="outlined" sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="formulation tabs">
            <Tab label="Select" {...a11yProps(0)} />
            <Tab label="Analysis" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 2 }}>
            <DatabaseFormulationSelector
              onFormulationSelect={handleFormulationSelect}
              onCreateNew={handleCreateNewFormulation}
              selectedFormulationId={selectedFormulation?.id}
            />

            {showCreateForm && (
              <Box sx={{ mt: 3 }}>
                <FormulationCreator
                  onSave={handleFormulationSave}
                  waterProfile={waterProfile}
                />
              </Box>
            )}

            {/* Confirmation Card */}
            {selectedFormulation && !isStage2Completed && (
              <Card sx={{ mt: 3, border: '2px solid', borderColor: 'success.main' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    Confirm Formulation Selection
                  </Typography>

                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Selected:</strong> {selectedFormulation.name}
                      {selectedFormulation.cropType && ` (${selectedFormulation.cropType})`}
                    </Typography>
                    {selectedFormulation.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedFormulation.description}
                      </Typography>
                    )}
                  </Alert>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click "Confirm & Continue" to save this formulation to your workflow and proceed to salt library selection.
                  </Typography>

                  {selectedFormulation.elements && selectedFormulation.elements.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Target Elements: {selectedFormulation.elements.length} nutrients defined
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedFormulation(null)}
                  >
                    Select Different Formulation
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleConfirmFormulation}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ minWidth: 200 }}
                  >
                    Confirm & Continue to Salt Library
                  </Button>
                </CardActions>
              </Card>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2 }}>
            <FormulationAnalysis />
          </Box>
        </TabPanel>
      </Paper>
    </WorkflowLayout>
  );
};

export default FormulationWorkflowPage;
